import type { CameraProfile } from '@/types/route.types'

/**
 * Camera sensor database for photogrammetry GSD calculations.
 * Each profile stores the physical sensor specs needed to compute
 * ground sampling distance, photo footprint, and line/photo spacing.
 *
 * sensorWidth / sensorHeight  → physical sensor size (mm)
 * focalLength                 → actual focal length (mm), NOT 35mm equiv.
 * imageWidth / imageHeight    → native resolution (px)
 * minShutterInterval          → fastest reliable interval between shots (s)
 */
export const cameraProfiles: CameraProfile[] = [
  {
    id: 'mavic3e',
    name: 'Mavic 3E (Wide)',
    sensorWidth: 17.3,
    sensorHeight: 13,
    focalLength: 12.3,
    imageWidth: 5280,
    imageHeight: 3956,
    minShutterInterval: 0.7,
  },
  {
    id: 'mavic3t',
    name: 'Mavic 3T (Wide)',
    sensorWidth: 6.16,
    sensorHeight: 4.55,
    focalLength: 4.4,
    imageWidth: 8000,
    imageHeight: 6000,
    minShutterInterval: 0.7,
    irProfile: { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 9.1, imageWidth: 640, imageHeight: 512 },
  },
  {
    id: 'mavic3m',
    name: 'Mavic 3M (Multispectral)',
    sensorWidth: 6.16,
    sensorHeight: 4.62,
    focalLength: 4.4,
    imageWidth: 2592,
    imageHeight: 1944,
    minShutterInterval: 0.7,
  },
  {
    id: 'matrice30',
    name: 'Matrice 30 (Wide)',
    sensorWidth: 6.16,
    sensorHeight: 4.55,
    focalLength: 4.48,
    imageWidth: 4000,
    imageHeight: 3000,
    minShutterInterval: 1.0,
    irProfile: { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 9.1, imageWidth: 640, imageHeight: 512 },
  },
  {
    id: 'matrice300rtk-h20',
    name: 'Matrice 300 RTK (H20 Wide)',
    sensorWidth: 6.16,
    sensorHeight: 4.55,
    focalLength: 4.5,
    imageWidth: 4056,
    imageHeight: 3040,
    minShutterInterval: 1.0,
  },
  {
    id: 'matrice350rtk-h20t',
    name: 'Matrice 350 RTK (H20T Wide)',
    sensorWidth: 6.16,
    sensorHeight: 4.55,
    focalLength: 4.5,
    imageWidth: 4056,
    imageHeight: 3040,
    minShutterInterval: 1.0,
    irProfile: { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 9.1, imageWidth: 640, imageHeight: 512 },
  },
  {
    id: 'matrice3d',
    name: 'Matrice 3D (Wide)',
    sensorWidth: 17.3,
    sensorHeight: 13,
    focalLength: 12.3,
    imageWidth: 5280,
    imageHeight: 3956,
    minShutterInterval: 0.7,
  },
  {
    id: 'matrice3td',
    name: 'Matrice 3TD (Wide)',
    sensorWidth: 6.16,
    sensorHeight: 4.55,
    focalLength: 4.4,
    imageWidth: 8000,
    imageHeight: 6000,
    minShutterInterval: 0.7,
    irProfile: { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 9.1, imageWidth: 640, imageHeight: 512 },
  },
  {
    id: 'phantom4rtk',
    name: 'Phantom 4 RTK',
    sensorWidth: 13.2,
    sensorHeight: 8.8,
    focalLength: 8.8,
    imageWidth: 5472,
    imageHeight: 3648,
    minShutterInterval: 0.7,
  },
  {
    id: 'matrice4d',
    name: 'Matrice 4D (Wide)',
    sensorWidth: 17.3,
    sensorHeight: 13,
    focalLength: 12.29,
    imageWidth: 5280,
    imageHeight: 3956,
    minShutterInterval: 0.7,
  },
  {
    id: 'matrice4td',
    name: 'Matrice 4TD (Wide)',
    sensorWidth: 6.16,
    sensorHeight: 4.55,
    focalLength: 4.4,
    imageWidth: 8000,
    imageHeight: 6000,
    minShutterInterval: 0.7,
    irProfile: { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 9.1, imageWidth: 640, imageHeight: 512 },
  },
]

/**
 * Maps droneEnumValue → compatible camera profile IDs.
 * Fixed-camera drones (M3D, M4D, etc.) have 1-2 profiles;
 * interchangeable-payload drones (M300, M350) list the common payload.
 * First entry is the default selection for that drone.
 */
export const DRONE_CAMERA_MAP: Record<number, string[]> = {
  77: ['mavic3e', 'mavic3t', 'mavic3m'],
  67: ['matrice30'],
  60: ['matrice300rtk-h20'],
  89: ['matrice350rtk-h20t'],
  91: ['matrice3d', 'matrice3td'],
  100: ['matrice4d', 'matrice4td'],
}

export function getCameraProfilesForDrone(droneEnumValue: number | undefined): CameraProfile[] {
  if (droneEnumValue == null || !(droneEnumValue in DRONE_CAMERA_MAP)) return cameraProfiles
  const ids = DRONE_CAMERA_MAP[droneEnumValue]
  return cameraProfiles.filter((c) => ids.includes(c.id))
}

export function getDefaultCameraId(droneEnumValue: number | undefined): string {
  if (droneEnumValue != null && droneEnumValue in DRONE_CAMERA_MAP) {
    return DRONE_CAMERA_MAP[droneEnumValue][0]
  }
  return DEFAULT_AREA_PARAMS.cameraProfileId
}

/** Default parameters for a new area (photogrammetry) mission */
export const DEFAULT_AREA_PARAMS = {
  altitude: 50,
  forwardOverlap: 80,
  sideOverlap: 70,
  courseAngle: 0,
  margin: 0,
  speed: 7,
  flightPattern: 'ortho' as const,
  cameraProfileId: 'mavic3e',
  gimbalPitch: -90,    // nadir
  terrainFollow: false,
  uponCompletion: 'backToStart' as const,
  safeTakeoffAltitude: 20,
  photoMode: 'distance' as const,
  lensSelection: 'wide' as const,
  altitudeMode: 'ALT' as const,
  elevationOptimize: true,
  takeoffSpeed: 15,

  // Phase 2: Oblique defaults (FH2 reference: ortho 85/75, inclined 80/70, gimbal -45°, speed 15 m/s)
  obliqueForwardOverlap: 80,
  obliqueSideOverlap: 70,
  obliqueGimbalPitch: -45,
  obliqueSpeedSeparate: false,
  obliqueSpeed: 15,
  smartObliqueMode: false,  // Phase E: FH2-style startSmartOblique when true

  // Phase 3: Advanced defaults
  routeStartVertexIndex: -1,     // -1 = auto (grid generator default)
  takeoffRefPoint: null,         // null = not set (use aircraft position)
}
